import { Injectable, Optional } from '@angular/core';
import { getToken } from 'firebase/app-check';
import {
  Functions,
  httpsCallable,
  HttpsCallableOptions,
  HttpsCallableResult,
} from 'firebase/functions';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { firstValueFrom } from 'rxjs';
import { appCheck, functions } from '../app.component';

@Injectable({
  providedIn: 'root',
})
export class PublicFunctionsService {
  functions = functions;
  appCheck = appCheck;

  constructor(@Optional() private recaptchaV3Service?: ReCaptchaV3Service) {}

  async getRecaptchaToken(action: string): Promise<string> {
    if (!this.recaptchaV3Service) {
      return '';
    }
    try {
      return (await firstValueFrom(this.recaptchaV3Service.execute(action))) || '';
    } catch {
      return '';
    }
  }

  async getAppCheckToken(): Promise<string> {
    if (!this.appCheck) {
      return '';
    }
    try {
      const result = await getToken(this.appCheck, false);
      return result?.token || '';
    } catch {
      return '';
    }
  }

  async getSecurityHeaders(action?: string): Promise<Record<string, string>> {
    const [appCheckToken, recaptchaToken] = await Promise.all([
      this.getAppCheckToken(),
      action ? this.getRecaptchaToken(action) : Promise.resolve(''),
    ]);

    const headers: Record<string, string> = {};
    if (appCheckToken) {
      headers['X-Firebase-AppCheck'] = appCheckToken;
    }
    if (recaptchaToken) {
      headers['X-Recaptcha-Token'] = recaptchaToken;
    }
    return headers;
  }

  async callFunction<TReq extends Record<string, unknown>, TRes = unknown>(
    functionsInstance: Functions,
    endpointName: string,
    action: string,
    payload: TReq,
    options?: HttpsCallableOptions,
  ): Promise<HttpsCallableResult<TRes>> {
    const recaptchaToken = await this.getRecaptchaToken(action);
    const callable = httpsCallable<TReq & { recaptchaToken: string }, TRes>(
      functionsInstance,
      endpointName,
      options,
    );
    return callable({
      ...payload,
      recaptchaToken,
    });
  }
}
